import { Dispatch, FormEvent, SetStateAction, useCallback, useEffect, useState } from "react";

import {
  InteractionObjectType,
  InteractionType,
  ObjectDocumentObjectType,
  ObjectDocumentSource,
  ProjectIssuanceTransactionConstants,
} from "../../../../constants";
import { PillData, ProjectIssuanceTransactionPanelData, UploadedDocument } from "../../../../models";
import { updateIssuanceTransaction } from "../../../../service/project";
import { searchInteractions, SearchIssuanceTransactionsResponse } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { convertDateToStringDDMMYYYY, isOwnFormSubmit, useIsLoadingWrapper } from "../../../../utils";
import { useObjectAttachments } from "../../../../utils/hooks/useObjectAttachments";
import { dataGridMapFilterCriteria, DocumentListDocument, Toast } from "../../../../widget";
import { InteractionTag } from "../../../TagsPanel/models";
import { useIssuanceTransactionPermission } from "../../hooks/useIssuanceTransactionPermission";

interface UseEditIssuanceTransactionPanelProps {
  issuanceTransaction: SearchIssuanceTransactionsResponse["results"][0];
  show: boolean;
  onClose: () => void;
  handleRefresh: () => void;
}

interface UseEditIssuanceTransactionPanelReturnData {
  issuanceTransactionPanelData: ProjectIssuanceTransactionPanelData;
  setIssuanceTransactionPanelData: Dispatch<SetStateAction<ProjectIssuanceTransactionPanelData>>;
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  handleClosePanel: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;

  showTagsPanel: boolean;
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;
  tagPills: PillData[];
  setRefreshTags: Dispatch<SetStateAction<boolean>>;

  documents: DocumentListDocument[];
  canManageAttachments: boolean;
  // Whether the form itself may be edited and saved — false on a project the caller cannot manage issuances for.
  canEdit: boolean;
  projectUuid: string | null;
  showUploadDocumentModal: boolean;
  handleAddAttachment: () => void;
  handleCloseUploadDocumentModal: () => void;
  handleDocumentsUploaded: (documents: UploadedDocument[]) => Promise<void>;
  handleRemoveAttachment: (documentUuid: string) => Promise<void>;
  handleDownloadAttachment: (documentUuid: string) => Promise<void>;
}

/*
  The picker shows the stored name rather than forcing a re-selection (BR-02831/02832). The uuid is gated on
  MANAGE_COLLABORATOR server-side, so a transaction the caller cannot manage the company for arrives as a
  name with nothing behind it — see TransactionCompanyPicker.
*/
const seedPanelData = (
  issuanceTransaction: SearchIssuanceTransactionsResponse["results"][0]
): ProjectIssuanceTransactionPanelData => ({
  ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
  issuanceUuid: issuanceTransaction.issuanceUuid,
  parentTransactionUuid: issuanceTransaction.parentTransactionUuid,
  counterPartyCollaboratorUuid: issuanceTransaction.counterParty.uuid,
  counterPartyName: issuanceTransaction.counterParty.name,
  brokerCollaboratorUuid: issuanceTransaction.broker.uuid,
  brokerName: issuanceTransaction.broker.name,
  quantity: issuanceTransaction.quantity,
  price: issuanceTransaction.price,
  fees: issuanceTransaction.fees,
  totalValue: issuanceTransaction.totalValue,
  areaHa: issuanceTransaction.areaHa,
  externalReference: issuanceTransaction.externalReference,
  rowVersion: issuanceTransaction.rowVersion,
  transactionDate: issuanceTransaction.transactionDate,
  expiryDate: issuanceTransaction.expiryDate,
  uuid: issuanceTransaction.uuid,
  type: issuanceTransaction.type,
});

export const useEditIssuanceTransactionPanel = ({
  issuanceTransaction,
  show,
  onClose,
  handleRefresh,
}: UseEditIssuanceTransactionPanelProps): UseEditIssuanceTransactionPanelReturnData => {
  const { currentOrganisationUuid } = useAuth();

  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);
  const [showTagsPanel, setShowTagsPanel] = useState(false);
  const [tagPills, setTagPills] = useState<PillData[]>([]);
  const [refreshTags, setRefreshTags] = useState(false);
  const [showUploadDocumentModal, setShowUploadDocumentModal] = useState(false);
  const [uploadToken, setUploadToken] = useState(0);

  const projectUuid = issuanceTransaction.project?.uuid ?? null;
  /*
    Resolved rather than read from ProjectContext alone: the org-wide inventory dashboard renders this panel
    outside ProjectProvider, where the context cannot answer. See useIssuanceTransactionPermission.
  */
  const { canManageIssuances } = useIssuanceTransactionPermission(projectUuid, show);
  const canManageAttachments = canManageIssuances;

  // The transaction already exists, so an upload is linked to it as soon as it lands.
  const {
    attachments,
    beginUpload,
    handleDocumentsUploaded: linkUploadedDocuments,
    handleRemoveAttachment,
    handleDownloadAttachment,
    resetAttachments,
  } = useObjectAttachments({
    objectUuid: issuanceTransaction.uuid,
    objectType: ObjectDocumentObjectType.ISSUANCE_TRANSACTION,
    documentSource: ObjectDocumentSource.PROJECT_DOCUMENT,
    documentScope: "project",
    parentUuid: projectUuid,
    subjectLabel: "transaction",
  });

  const documents: DocumentListDocument[] = attachments.map((attachment) => ({
    documentUuid: attachment.documentUuid,
    fileName: attachment.fileName,
    documentType: attachment.documentType ?? undefined,
    uploadedAt: attachment.uploadedAt ? convertDateToStringDDMMYYYY(attachment.uploadedAt) : undefined,
    url: attachment.url ?? undefined,
  }));

  const handleAddAttachment = (): void => {
    setUploadToken(beginUpload());
    setShowUploadDocumentModal(true);
  };

  const handleCloseUploadDocumentModal = (): void => {
    setShowUploadDocumentModal(false);
  };

  const handleDocumentsUploaded = async (uploaded: UploadedDocument[]): Promise<void> => {
    setShowUploadDocumentModal(false);
    await linkUploadedDocuments(uploaded, uploadToken);
  };

  const [issuanceTransactionPanelData, setIssuanceTransactionPanelData] = useState<ProjectIssuanceTransactionPanelData>(
    () => seedPanelData(issuanceTransaction)
  );

  const handleClosePanel = (): void => {
    onClose();
    setIssuanceTransactionPanelData({
      ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
    });
    setShowTagsPanel(false);
    setShowUploadDocumentModal(false);
    setTagPills([]);
    setErrors([]);
    resetAttachments();
  };
  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!isOwnFormSubmit(e)) return;
    setErrors([]);

    // The form is disabled without the grant, so this only catches a submit fired before the read landed.
    if (!canManageIssuances) return;

    const res = await updateIssuanceTransaction({
      areaHa: issuanceTransactionPanelData.areaHa,
      counterPartyCollaboratorUuid: issuanceTransactionPanelData.counterPartyCollaboratorUuid,
      brokerCollaboratorUuid: issuanceTransactionPanelData.brokerCollaboratorUuid,
      expiryDate: issuanceTransactionPanelData.expiryDate,
      externalReference: issuanceTransactionPanelData.externalReference,
      fees: issuanceTransactionPanelData.fees,
      price: issuanceTransactionPanelData.price,
      quantity: issuanceTransactionPanelData.quantity ?? 0,
      totalValue:
        issuanceTransactionPanelData.totalValue !== null
          ? parseFloat(issuanceTransactionPanelData.totalValue.toFixed(5))
          : null,
      transactionDate: issuanceTransactionPanelData.transactionDate,
      uuid: issuanceTransactionPanelData.uuid ?? "",
      rowVersion: issuanceTransactionPanelData.rowVersion ?? 0,
    });

    if (res.status === Status.Success) {
      Toast.success({ message: "Issuance transaction updated successfully" });
      handleRefresh();
      handleClosePanel();
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  const fetchTags = useCallback(
    async (uuid: string, orgUuid: string): Promise<void> => {
      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.type = {
        operator: "eq",
        value: InteractionType.TAG,
      };
      filterCriteria.objectType = {
        operator: "eq",
        value: InteractionObjectType.ORGANISATION,
      };
      filterCriteria.objectUuid = {
        operator: "eq",
        value: orgUuid,
      };
      filterCriteria.objectInteractions = {
        some: {
          objectUuid: {
            operator: "eq",
            value: uuid,
          },
          endDate: {
            operator: "eq",
            value: null,
          },
        },
      };
      const response = await searchInteractions({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 50,
        },
        filter: { results: filterCriteria },
      });

      if (response.data?.results != null) {
        const existingTags =
          response.data.results.map(
            (x) =>
              ({
                uuid: x.uuid,
                data: JSON.parse(x.data),
                checked: true,
                rowVersion: x.rowVersion,
              }) as InteractionTag
          ) ?? [];
        setTagPills(existingTags.map((tag) => ({ key: tag.uuid, label: tag.data.name })));
      }
    },
    [issuanceTransaction.uuid, currentOrganisationUuid, refreshTags]
  );

  useEffect(() => {
    handleRefresh();
  }, [issuanceTransaction.uuid]);

  useEffect(() => {
    if (currentOrganisationUuid) {
      fetchTags(issuanceTransaction.uuid, currentOrganisationUuid);
    }
  }, [issuanceTransaction.uuid, currentOrganisationUuid, refreshTags]);

  return {
    errors,
    isHandleSubmitLoading,
    issuanceTransactionPanelData,
    setIssuanceTransactionPanelData,
    handleClosePanel,
    handleSubmit,
    showTagsPanel,
    setShowTagsPanel,
    tagPills,
    setRefreshTags,
    documents,
    canManageAttachments,
    canEdit: canManageIssuances,
    projectUuid,
    showUploadDocumentModal,
    handleAddAttachment,
    handleCloseUploadDocumentModal,
    handleDocumentsUploaded,
    handleRemoveAttachment,
    handleDownloadAttachment,
  };
};
